/**
 * Org User Service — calls the HUGO Cloud Function `hugoCreateOrgUser` to create a
 * real Firebase Auth account for a new member and assign their role in one step.
 *
 * The browser can't create other users' accounts directly (createUser would sign
 * the admin out), so this goes through the callable Cloud Function, which uses the
 * Admin SDK server-side and returns a temporary password to share.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase';

// Match the function's deployed region (see functions/src/hugoCreateOrgUser.js).
const functions = getFunctions(app, 'us-central1');

/**
 * Provision a new org member: creates/links the Auth account and assigns the role.
 * @param {Object} params
 * @param {string} params.firstName
 * @param {string} params.lastName
 * @param {string} params.email
 * @param {string} params.role - org_admin | site_manager | department_lead | team_lead | respondent
 * @param {string|null} params.scopeId - site/department/team id for scoped roles
 * @param {string} params.organizationId
 * @returns {Promise<{success:boolean, uid:string, email:string, displayName:string,
 *   role:string, isNewUser:boolean, tempPassword:string|null}>}
 */
export async function provisionOrgUser({ firstName, lastName, displayName = '', title = '', email, role, scopeId = null, scopeIds = null, siteId = null, deptId = null, nwtId = null, notify = true, organizationId }) {
  const callable = httpsCallable(functions, 'hugoCreateOrgUser');
  const res = await callable({ firstName, lastName, displayName, title, email, role, scopeId, scopeIds, siteId, deptId, nwtId, notify, organizationId });
  return res.data;
}

/**
 * Change an existing member's role/scope, or remove their membership.
 * Goes through the `hugoUpdateOrgMember` callable so org admins (not just
 * superadmins) can manage members — client-side Hugo_ToolAccess writes are
 * blocked by Firestore rules by design.
 * @param {Object} params
 * @param {string} params.organizationId
 * @param {string} params.uid - Target member's uid
 * @param {'assign'|'remove'} params.action
 * @param {string} [params.role] - Required for 'assign'
 * @param {string|null} [params.scopeId] - Required for scoped roles
 */
export async function updateOrgMember({ organizationId, uid, action, role = null, scopeId = null, scopeIds = null, siteId = null, deptId = null, nwtId = null, firstName, lastName, displayName, title }) {
  const callable = httpsCallable(functions, 'hugoUpdateOrgMember');
  const res = await callable({ organizationId, uid, action, role, scopeId, scopeIds, siteId, deptId, nwtId, firstName, lastName, displayName, title });
  return res.data;
}

/**
 * Edit a member's profile fields (P4-27): first/last/display name, title, photoURL. Self-service
 * for the signed-in user (organizationId optional); org admins pass the member's uid + org.
 */
export async function updateMemberProfile({ uid, organizationId = null, firstName, lastName, displayName, title, photoURL }) {
  const callable = httpsCallable(functions, 'hugoUpdateOrgMember');
  const res = await callable({ uid, organizationId, action: 'profile', firstName, lastName, displayName, title, photoURL });
  return res.data;
}
